import { useState } from 'react';
import {
  RiFingerprintLine,
  RiLockPasswordLine,
  RiMailLine,
  RiShieldCheckLine,
  RiSparkling2Line,
} from 'react-icons/ri';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuthStore } from '../state/authStore';

const DEMO = { username: 'demo', password: 'Expotar2026' };

const FEATURES = [
  { icon: RiShieldCheckLine, title: '授权门与留痕', desc: '立案、范围校验、全程审计，默认不对外暴露数据' },
  { icon: RiSparkling2Line, title: '研判与操作规划', desc: 'AI 产出可执行动作，人工确认后批量应用、可撤销' },
  { icon: RiFingerprintLine, title: '关联墙与时间线', desc: '红线绳、图钉、圈注、手绘组成的侦探工作台' },
];

export function LoginScreen() {
  const status = useAuthStore(state => state.status);
  const busy = useAuthStore(state => state.busy);
  const error = useAuthStore(state => state.error);
  const login = useAuthStore(state => state.login);
  const clearError = useAuthStore(state => state.clearError);

  const [form, setForm] = useState({ identifier: '', password: '' });

  const submit = async () => {
    if (!form.identifier.trim() || !form.password) return;
    await login(form.identifier.trim(), form.password);
  };

  return (
    <div className="grid min-h-full lg:grid-cols-[1.05fr_1fr]">
      {/* 品牌侧 */}
      <div className="relative hidden overflow-hidden bg-[#0f1a2e] p-10 text-white lg:flex lg:flex-col lg:justify-between">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.16]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
        <div
          className="pointer-events-none absolute -right-24 -top-24 size-[420px] rounded-full opacity-30 blur-3xl"
          style={{ background: 'radial-gradient(circle, #38bdf8, transparent 70%)' }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-16 size-[380px] rounded-full opacity-25 blur-3xl"
          style={{ background: 'radial-gradient(circle, #a78bfa, transparent 70%)' }}
        />

        <div className="relative flex items-center gap-3">
          <div className="grid size-10 place-items-center rounded-[10px] bg-white/10 ring-1 ring-white/20">
            <RiFingerprintLine className="size-5" />
          </div>
          <div>
            <div className="text-[15px] font-semibold tracking-[0.18em]">EXPOTAR</div>
            <div className="text-[11px] text-white/60">情报研判工作台</div>
          </div>
        </div>

        <div className="relative max-w-[420px]">
          <h1 className="text-[26px] font-semibold leading-snug tracking-tight">
            把公开信息整理成
            <br />
            一张能推理的关联墙
          </h1>
          <p className="mt-3 text-[12.5px] leading-relaxed text-white/65">
            面向已获书面授权的防御性评估：红队演练、自有资产暴露面自查、安全意识培训复盘。
          </p>
          <div className="mt-8 flex flex-col gap-3">
            {FEATURES.map(item => (
              <div key={item.title} className="flex gap-3">
                <div className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-[7px] bg-white/8 ring-1 ring-white/15">
                  <item.icon className="size-3.5" />
                </div>
                <div>
                  <div className="text-[12.5px] font-medium">{item.title}</div>
                  <div className="text-[11px] leading-relaxed text-white/55">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-[10.5px] leading-relaxed text-white/40">
          账号与案件数据按用户隔离；自助注册已关闭，账号由平台管理员开户；
          共享演示为独立只读通道，不返回会话、密钥与审计信息。
        </p>
      </div>

      {/* 表单侧 */}
      <div className="flex items-center justify-center bg-background px-6 py-10">
        <div className="w-full max-w-[380px]">
          <div className="mb-6 flex items-center gap-2 lg:hidden">
            <div className="grid size-9 place-items-center rounded-[9px] bg-primary text-primary-foreground">
              <RiFingerprintLine className="size-4" />
            </div>
            <div>
              <div className="text-[13px] font-semibold tracking-[0.14em]">EXPOTAR</div>
              <div className="text-[10px] text-muted-foreground">情报研判工作台</div>
            </div>
          </div>

          <h2 className="text-[20px] font-semibold tracking-tight">登录工作台</h2>
          <p className="mt-1 text-[12px] text-muted-foreground">
            使用邮箱或用户名登录。账号由平台管理员开户，不开放自助注册。
          </p>

          <div className="mt-6 flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>邮箱或用户名</Label>
              <div className="relative">
                <RiMailLine className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-8"
                  autoComplete="username"
                  placeholder="analyst@example.com 或 analyst"
                  value={form.identifier}
                  onChange={e => {
                    clearError();
                    setForm({ ...form, identifier: e.target.value });
                  }}
                  onKeyDown={e => e.key === 'Enter' && submit()}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>口令</Label>
              <div className="relative">
                <RiLockPasswordLine className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-8"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e => {
                    clearError();
                    setForm({ ...form, password: e.target.value });
                  }}
                  onKeyDown={e => e.key === 'Enter' && submit()}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-[var(--radius-sm)] border border-[var(--color-cord)]/30 bg-[var(--color-cord)]/6 px-2.5 py-2 text-[11.5px] text-[var(--color-cord)]">
                {error}
              </div>
            )}

            <Button className="mt-1 h-9" disabled={busy || status === 'loading'} onClick={submit}>
              {busy ? '处理中…' : '登录'}
            </Button>

            <div className="flex items-center gap-2 py-1">
              <span className="h-px flex-1 bg-border" />
              <span className="text-[10px] text-muted-foreground">或</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <Button
              variant="outline"
              className="h-9"
              disabled={busy}
              onClick={() => login(DEMO.username, DEMO.password)}
            >
              <RiSparkling2Line className="size-3.5 text-[var(--color-lead)]" />
              以演示账号进入
            </Button>

            <p className="mt-1 text-center text-[11px] text-muted-foreground">
              没有账号？请联系平台管理员开户
            </p>
          </div>

          <p className="mt-6 border-t border-dashed border-border pt-4 text-[10.5px] leading-relaxed text-muted-foreground">
            仅限合法授权场景使用。登录行为与后续操作均会写入审计日志。
          </p>
        </div>
      </div>
    </div>
  );
}